/**
 * Subscription-plan entitlements — the single source of truth for which
 * features each business plan unlocks.
 *
 * Framework-free (no zod, no @prisma, no @nestjs), so the API and both clients
 * (web + mobile) all import the SAME matrix and can never drift. "Flagging a
 * feature to a plan" is a one-line edit to `PLAN_FEATURES` here — nothing else
 * in the app hard-codes what is or isn't a premium capability.
 *
 * Model (settled with the product owner):
 *  - Plans are a BUSINESS concept only; end clients never have a plan.
 *  - Three paid tiers, no free tier: basic < pro < premium (ascending).
 *  - Per-business overrides (see `resolveEntitlements`) let a platform admin
 *    grant/revoke a single feature for one business (trials, promos) on top of
 *    its plan — a generalised `isRecommended`.
 *  - Boolean features only for v1 (no numeric quotas/limits yet).
 *
 * Enforcement is server-side (see the API's entitlements service +
 * `featureProcedure`); the clients only reflect this matrix for UX (show/hide
 * or teaser). The matrix is the source of truth; the client is never trusted.
 */

/** Business subscription tiers, ordered ascending (basic < pro < premium). */
export const PLANS = ['basic', 'pro', 'premium'] as const;
export type Plan = (typeof PLANS)[number];

/** Plan a brand-new business starts on (existing rows were backfilled to premium). */
export const DEFAULT_PLAN: Plan = 'basic';

/**
 * The feature registry — every GATEABLE capability in the app. Core booking
 * (calendar, clients, services, staff, public booking page, profile) is
 * deliberately NOT listed here: it's available on every plan and never gated,
 * so it has no entry to flag. A capability is "premium" only because the
 * matrix below places it there.
 */
export const FEATURES = [
  'clientChat',
  'reviews',
  'clientTags',
  'analytics',
  'reminders',
  'pageViewInsights',
  'waitlist',
  'ai',
  'recommendedPlacement',
] as const;
export type Feature = (typeof FEATURES)[number];

/**
 * plan → features it includes. THE source of truth for packaging; edit this to
 * re-flag a feature. `premium` is spread from `FEATURES` so a newly-added
 * feature is automatically in the top tier and can never be silently
 * unreachable (a test asserts this).
 */
export const PLAN_FEATURES: Record<Plan, readonly Feature[]> = {
  // Entry tier: core booking (always on, not in the registry) + the
  // relationship basics that make a business usable and competitive.
  basic: ['clientChat', 'reviews'],
  // Growth tools.
  pro: ['clientChat', 'reviews', 'clientTags', 'analytics', 'reminders', 'pageViewInsights'],
  // Everything.
  premium: [...FEATURES],
};

/**
 * A per-business override map (stored as `Business.featureOverrides` jsonb):
 * `true` force-grants a feature regardless of plan, `false` force-revokes it,
 * an absent key follows the plan. Rarely populated; set by platform admins.
 */
export type FeatureOverrides = Partial<Record<Feature, boolean>>;

/**
 * The effective feature set for a business = (its plan's features ∪ granted
 * overrides) − revoked overrides. Returned in stable `FEATURES` registry order
 * (not plan-list order) so callers get a deterministic array.
 */
export function resolveEntitlements(plan: Plan, overrides: FeatureOverrides = {}): Feature[] {
  const set = new Set<Feature>(PLAN_FEATURES[plan]);
  for (const f of FEATURES) {
    const o = overrides[f];
    if (o === true) set.add(f);
    else if (o === false) set.delete(f);
  }
  return FEATURES.filter((f) => set.has(f));
}

/** Whether a plan includes a feature by its base packaging (ignores overrides). */
export function planHasFeature(plan: Plan, feature: Feature): boolean {
  return PLAN_FEATURES[plan].includes(feature);
}

/**
 * The lowest tier whose base packaging unlocks `feature` — what an owner-facing
 * "upgrade to X" badge/teaser should display. Since `PLANS` is ascending and
 * `premium` holds every feature, this always resolves to a real tier.
 */
export function requiredTierFor(feature: Feature): Plan {
  for (const plan of PLANS) {
    if (PLAN_FEATURES[plan].includes(feature)) return plan;
  }
  return 'premium';
}

/** Runtime type guard for a plan string (e.g. validating a DB column). */
export function isPlan(value: unknown): value is Plan {
  return typeof value === 'string' && (PLANS as readonly string[]).includes(value);
}

/** Coerce an unknown (a DB read, an untyped input) to a Plan, falling back to the default. */
export function asPlan(value: unknown): Plan {
  return isPlan(value) ? value : DEFAULT_PLAN;
}

/**
 * Safely parse the `featureOverrides` jsonb column (unknown at the type
 * boundary) into a typed `FeatureOverrides`: keeps only known feature keys with
 * boolean values, drops everything else. Null/array/non-object → `{}`.
 */
export function parseFeatureOverrides(raw: unknown): FeatureOverrides {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  const out: FeatureOverrides = {};
  for (const f of FEATURES) {
    const v = record[f];
    if (typeof v === 'boolean') out[f] = v;
  }
  return out;
}
